<template>
  <div class="address-box" :class="{ 'card-layout': isCardLayout }">
    <div class="flex">
      <div class="flex items-center justify-end" style="width: 25%">
        <el-popover
          v-if="eSignsArr?.length > 0 || isMaskedSignature"
          :disabled="!isMaskedSignature"
          placement="bottom"
          :width="180"
          trigger="click"
        >
          <template #reference>
            <el-button type="primary" class="edit-button" style="position: relative" @click="onDisplaySignature">
              <IconSignature />
              <IconMaskedLock v-if="isMaskedSignature" class="absolute" style="bottom: 5px; right: 0" />
            </el-button>
          </template>
          <div style="background-color: white; color: #ff0000">{{ $t('Require View Permission') }}</div>
        </el-popover>
        <el-popover
          v-if="Object.keys(imageUrlsArr).length > 0 || isMaskedImage"
          :disabled="!isMaskedImage"
          placement="bottom"
          :width="180"
          trigger="click"
        >
          <template #reference>
            <el-button type="primary" class="edit-button" style="position: relative" @click="onDisplayActivateImage">
              <IconImage />
              <IconMaskedLock v-if="isMaskedImage" class="absolute" style="bottom: 5px; right: 0" />
            </el-button>
          </template>
          <div style="background-color: white; color: #ff0000">{{ $t('Require View Permission') }}</div>
        </el-popover>
      </div>
      <div class="flex items-center justify-center" style="width: 50%">
        <div class="label-box w-full" style="margin: 0 10px 0 10px" :class="{ 'tracking-point': !view }">
          {{ title }}
        </div>
      </div>
      <div class="flex items-center justify-start mx-1">
        <el-button v-if="isAuth && view && editable" type="primary" class="edit-button" @click="onClickEdit">
          <IconEdit />
        </el-button>
      </div>
      <div class="flex items-center justify-start mx-1">
        <el-button
          v-if="isAuth && view && hasReferenceFields"
          type="primary"
          class="edit-button"
          @click="onClickRefreshReferenceField"
        >
          <i class="el-icon-refresh"></i>
        </el-button>
      </div>
    </div>

    <template v-if="isCardLayout">
      <div class="label-and-value" v-for="(value, label) in dataList" :key="label">
      <!-- Custom fields not have translate, so should not translate -->
      <div
        class="label"
        v-if="isTranslateAvailable(toSnakeCase(value.label))"
      >{{ $t(toSnakeCase(value.label)) }}</div>
      <div class="label" v-else>{{ value.label }}</div>
      <div class="value-cell">
        <div
          v-if="isAdjustmentField(value.fieldType)"
          class="pl-2 adjustment-link"
          @click="onShowAdjustment(label, value)"
          v-html="getDisplayValue(value.value, value.fieldType)"
        ></div>
        <div v-else-if="isListStringField(value.fieldType) && Array.isArray(value.value)" class="value">
          <div
            class="label-box"
            style="margin-bottom: 0px; cursor: pointer;"
            @click="showListStringModal(value.value, value.label)"
          >
            {{ value.value.length }} {{ value.value.length === 1 ? $t('item') : $t('items') }}
          </div>
        </div>
        <div v-else-if="isListObjectField(value.fieldType) && Array.isArray(value.value)" class="value">
          <ListObjectView
            mode="modal"
            :items="withLocalFileUrls(value.value)"
            :subFields="value.subFields || {}"
            :label="value.label"
          />
        </div>
        <div
          v-else
          v-linkify
          :class="['value', getDisplayValue(value.value) === MASKED_ANNOTATION ? 'masked-value' : '']"
          v-html="getDisplayValue(value.value, value.fieldType)"
        ></div>
      </div>
    </div>
    </template>

    <template v-else>
      <div class="label-and-value" v-for="(value, label) in dataList" :key="label">
      <!-- Custom fields not have translate, so should not translate -->
      <div class="label" v-if="isTranslateAvailable(toSnakeCase(value.label))">
        {{ $t(toSnakeCase(value.label)) }}
      </div>
      <div class="label" v-else>{{ value.label }}</div>
      <div>
        <div
          v-if="isAdjustmentField(value.fieldType)"
          class="pl-2 adjustment-link"
          @click="onShowAdjustment(label, value)"
          v-html="getDisplayValue(value.value, value.fieldType)"
        ></div>
        <div v-else-if="isListStringField(value.fieldType) && Array.isArray(value.value)" class="value">
          <div
            class="label-box"
            style="margin-bottom: 0px; cursor: pointer;"
            @click="showListStringModal(value.value, value.label)"
          >
            {{ value.value.length }} {{ value.value.length === 1 ? $t('item') : $t('items') }}
          </div>
        </div>
        <div v-else-if="isListObjectField(value.fieldType) && Array.isArray(value.value)" class="value">
          <ListObjectView
            mode="modal"
            :items="withLocalFileUrls(value.value)"
            :subFields="value.subFields || {}"
            :label="value.label"
          />
        </div>
        <div
          v-else
          v-linkify
          :class="['value', getDisplayValue(value.value) === MASKED_ANNOTATION ? 'masked-value' : '']"
          v-html="getDisplayValue(value.value, value.fieldType)"
        ></div>
      </div>
    </div>
    </template>

    <div v-if="isFilesExist">
      <div v-for="(value, key) in fileList" :key="key" class="file-and-value">
        <div class="text-right">{{ value?.label }} :</div>
        <a>
          <div class="text-left" v-for="(el, idx) in value?.files" :key="`${key}_${idx}`">
            <div @click="onPreview(el.url)">
              <IconListFile />
              <div class="w-full py-2.5">
                <span>{{ el?.name }}</span>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  </div>
  <ImageViewerDialog v-model:imageDialogVisible="imageDialogVisible" :image="imageObj" />
  <AdjustmentDataDialog v-model:isShow="isShowAdjustmentField" :adjustmentField="adjustmentField" />
  <!-- List String Modal -->
  <el-dialog
    v-model="listStringModalVisible"
    :title="currentFieldLabel"
    width="100%"
    center
    custom-class="list-string-modal"
    :show-close="false"
  >
    <div>
      <div style="margin-bottom: 16px; font-weight: bold;">
        {{ $t('total') }}: {{ listStringData.length }} {{ listStringData.length === 1 ? $t('item') : $t('items') }}
      </div>
      <div style="max-height: 400px; overflow-y: auto;">
        <table style="width: 100%; border-collapse: collapse;">
          <tbody>
            <tr v-for="(item, index) in listStringData" :key="index">
              <td style="border: 1px solid #ddd; padding: 8px;">#{{ index + 1 }}</td>
              <td style="border: 1px solid #ddd; padding: 8px;">{{ item.value }}</td>
              <td style="border: 1px solid #ddd; padding: 8px;">{{ formatDateTime(item.createdDateTime) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <template #footer>
      <el-button @click="listStringModalVisible = false" type="primary">
        {{ $t('ok') }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    center
    top="0"
    :model-value="signatureDialogVisible"
    :title="eSignCarouselHeaderLabel"
    @closed="signatureDialogVisible = false"
  >
    <div>
      <el-carousel @change="onESignCarouselChange" arrow="always" :autoplay="false" indicator-position="none">
        <el-carousel-item v-for="(eSign, key) in eSignsArr" :key="key">
          <div style="border: 1px solid #c4c4c4; place-self: center">
            <img :src="eSign.eSign" alt="eSign" />
          </div>
        </el-carousel-item>
      </el-carousel>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button id="AppDialog_closeDialog" type="primary" @click="signatureDialogVisible = false">
          {{ $t('ok') }}
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import ImageViewerDialog from 'smartbarcode-web-core/src/components/ImageViewerDialog.vue'
import IconEdit from '@/components/svg/IconEdit.vue'
import IconImage from '@/components/svg/IconImage.vue'
import IconListFile from 'smartbarcode-web-core/src/components/svg/IconListFile.vue'
import IconMaskedLock from '@/components/svg/IconMaskedLock.vue'
import IconSignature from '@/components/svg/IconSignature.vue'
import { getFilePathFromURL, toSnakeCase } from '@/utils/helpers'
import { localFileUrl } from '@/utils/localFileUrls'
import { isEmpty } from 'smartbarcode-web-core/src/utils/typeChecker'
import {
  ICustomFieldData,
  IDynamicCustomFieldData,
  IFile,
  ILabelDisplay,
  IListObjectItem,
  IListStringItem,
} from 'smartbarcode-web-core/src/utils/types/index'
import cloneDeep from 'lodash/cloneDeep'
import { Options, Vue } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import { DATE_TIME, MASKED_ANNOTATION } from 'smartbarcode-web-core/src/utils/constants'
import { objectToArray, arrayToObject } from 'smartbarcode-web-core/src/utils/helpers'
import { Linkify } from '@/utils/directives'
import { ECustomFieldType } from 'smartbarcode-web-core/src/utils/enums'
import AdjustmentDataDialog from '@/components/dialog/AdjustmentDataDialog.vue'
import ListObjectView from 'smartbarcode-web-core/src/lib-components/ListObjectView.vue'
import { ICustomFieldShowData } from '@/store/type'
import { format } from 'date-fns'
import { ENTRY_VIEW } from '@/utils/constants'

export interface IFileInfo {
  files: {
    url: string
    name: string
  }[]
  label: string
}

@Options({
  directives: {
    linkify: Linkify,
  },
  components: {
    IconEdit,
    IconImage,
    IconMaskedLock,
    IconSignature,
    IconListFile,
    ImageViewerDialog,
    AdjustmentDataDialog,
    ListObjectView,
  },
  name: 'DetailAddressBox',
  methods: { toSnakeCase },
  emits: ['update:onRefreshReferenceFieldClick'],
})
export default class DetailAddressBox extends Vue {
  @Prop({ type: String }) readonly title!: string
  @Prop({ type: Object }) readonly eSigns!: ICustomFieldData[]
  @Prop({ type: Boolean }) readonly isMaskedImage!: boolean
  @Prop({ type: Boolean }) readonly isMaskedSignature!: boolean
  @Prop({ type: String }) readonly view?: string
  @Prop({ type: Object }) readonly data!: Record<string, ILabelDisplay>

  @Prop({ type: Object }) readonly fileLinks!: Record<string, ICustomFieldData>
  @Prop({ type: Object }) readonly fileImages!: Record<string, IDynamicCustomFieldData>
  @Prop({ type: Boolean }) readonly editable?: boolean
  @Prop({ type: Boolean }) readonly hasReferenceFields?: boolean
  localeList = Object.freeze(['ja', 'en', 'en-us', 'zh-tw', 'zh-cn'])

  imageDialogVisible = false
  signatureDialogVisible = false
  eSignCarouselIndex = 0
  imageCarouselIndex = 0
  image = {}
  isShowAdjustmentField = false
  adjustmentField = {} as ICustomFieldShowData
  listStringModalVisible = false
  listStringData: IListStringItem[] = []
  currentFieldLabel = ''

  get imageObj() {
    if (this.fileImages) {
      // FilterData use later
      const filterData = Object.values(this.fileImages).reduce((acc, curr) => {
        if (curr.files) {
          curr.files = curr.files.map((val) => {
            return { ...val, label: curr.label }
          })
          return { ...acc, [curr.label || '']: curr.files }
        }
        return acc
      }, {})
      return filterData || {}
    }
    return {}
  }

  get imageCarouselHeaderLabel() {
    return this.imageUrlsArr?.[this.imageCarouselIndex]?.label || ''
  }

  get eSignCarouselHeaderLabel() {
    return this.eSignsArr[this.eSignCarouselIndex]?.label || ''
  }

  get eSignsArr() {
    return this.eSigns?.filter((e) => !!e?.eSign) || []
  }

  get imageUrlsArr() {
    if (this.fileImages) {
      let data: IFile[] = []
      // FilterData use later
      Object.values(this.fileImages).map((curr) => {
        if (curr.files) {
          data = [...data, ...curr?.files]
        }
      }, {})
      return data
    }

    return []
  }

  get isQuickTrackingMode() {
    return this.$store.getters.isQuickTrackingModeEnabled
  }

  listHiddenKeysQuickTracking = ['externalId', 'center', 'maker']

  get dataList() {
    if (this.data) {
      const data = objectToArray(cloneDeep(this.data))
      return arrayToObject(
        data.filter((data) => {
          if (this.isQuickTrackingMode && this.listHiddenKeysQuickTracking.includes(data.key)) {
            return false
          }
          return !isEmpty(data.value.value)
        })
      )
    }
  }

  get isCardLayout() {
    return (
      !this.isQuickTrackingMode &&
      (this.view === ENTRY_VIEW.ORIGIN || this.view === ENTRY_VIEW.DESTINATION || this.view === ENTRY_VIEW.PACKAGE_INFO)
    )
  }

  get fileList(): { [key: string]: IFileInfo } {
    const fileLinks = this.fileLinks || {}
    const files = Object.keys(fileLinks).reduce((acc, currKey) => {
      if (!isEmpty(fileLinks?.[currKey].files)) {
        return { ...acc, [currKey]: this.fileLinks[currKey] }
      }
      if (fileLinks && !isEmpty(fileLinks?.[currKey]?.reference?.file)) {
        return { ...acc, [currKey]: { ...this.fileLinks[currKey], files: [this.fileLinks[currKey]?.reference?.file] } }
      }
      return acc
    }, {})
    return files
  }

  get isFilesExist() {
    return !isEmpty(this.fileList)
  }

  get MASKED_ANNOTATION() {
    return MASKED_ANNOTATION
  }

  get isAuth() {
    return this.$store.getters.isAuth
  }

  get project() {
    return this.$store.state.project.details
  }

  isAdjustmentField(fieldType?: ECustomFieldType) {
    return fieldType === ECustomFieldType.ADJUSTMENT
  }

  isListStringField(fieldType?: ECustomFieldType) {
    return fieldType === ECustomFieldType.LIST_STRING
  }

  isListObjectField(fieldType?: ECustomFieldType) {
    return fieldType === ECustomFieldType.LIST_OBJECT
  }

  onImageCarouselChange(idx: number) {
    this.imageCarouselIndex = idx
  }

  onESignCarouselChange(idx: number) {
    this.eSignCarouselIndex = idx
  }

  onShowAdjustment(cfKey: string, item: ILabelDisplay) {
    this.isShowAdjustmentField = true
    this.adjustmentField = {
      cfKey,
      label: item.label,
      value: item.value,
    }
  }

  // A just-submitted item carries only upload paths; the signed url arrives on
  // the next read, so show the local copy until then.
  withLocalFileUrls(items: IListObjectItem[]): IListObjectItem[] {
    return (items ?? []).map((item) => {
      const customFields = { ...(item?.customFields ?? {}) }
      Object.keys(customFields).forEach((code) => {
        const data = customFields[code]
        if (data?.fieldType !== ECustomFieldType.FILES) return
        customFields[code] = {
          ...data,
          files: (data.files ?? []).map((file) => ({ ...file, url: file.url || localFileUrl(file.path) })),
        }
      })
      return { ...item, customFields }
    })
  }

  onPreview(url: string) {
    window.open(url, '_blank')
  }

  onDisplayActivateImage() {
    if (this.isMaskedImage) return
    this.imageDialogVisible = true
  }

  onDisplaySignature() {
    if (this.isMaskedSignature) return
    this.signatureDialogVisible = true
  }

  getFileNameFromLink(linkFile: { url: string }) {
    return getFilePathFromURL(linkFile.url || '')
  }

  isTranslateAvailable(key: string) {
    let isFound = false
    if (!this.localeList) return
    this.localeList.forEach((val) => {
      if (this.$te(key, val)) {
        isFound = true
      }
    })
    return isFound
  }

  getDisplayValue(value: string | number | IListStringItem[], fieldType?: ECustomFieldType) {
    if (fieldType === ECustomFieldType.DATE_TIME_DATA && !Array.isArray(value)) {
      return format(new Date(value), DATE_TIME.WITH_SECOND)
    }
    return value
  }

  onClickEdit() {
    this.$router.push({ name: 'edit', params: { view: this.view } })
  }

  onClickRefreshReferenceField() {
    this.$emit('update:onRefreshReferenceFieldClick')
  }

  showListStringModal(data: IListStringItem[], fieldLabel: string) {
    if (Array.isArray(data)) {
      this.listStringData = data.map((item) => {
        if (typeof item === 'string') {
          return { value: item, createdDateTime: '' }
        }
        return item
      })
    } else {
      this.listStringData = []
    }
    this.currentFieldLabel = fieldLabel
    this.listStringModalVisible = true
  }

  formatDateTime(dateTime: string) {
    if (!dateTime) return ''
    try {
      return format(new Date(dateTime), DATE_TIME.WITH_SECOND)
    } catch (error) {
      return dateTime
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

::v-deep .list-string-modal {
  .el-dialog__header {
    padding-bottom: 0px;
  }
}

.masked-value {
  color: #ff0000;
}

.address-box {
  padding: 14px 0;

  .label-box {
    margin-bottom: 12px;
    display: inline-block;
  }
}

.el-button.edit-button {
  padding: 6px;

  svg {
    width: 16px;
    height: 16px;
  }
}

.tracking-point {
  background: $light-grey;
  padding: 4px 0;
  width: 100%;
  border: 0;
}

.label-and-value > .value {
  word-break: break-word;
}

.label-and-value > .label {
  word-break: break-word;

  &::after {
    content: ' :';
  }
}

.address-box.card-layout {
  padding-bottom: 12px;

  .label-and-value {
    flex-direction: column;
    align-items: stretch;
    padding: 0;
    margin: 12px 12px 0 12px;
    border: 1px solid #c4c4c4;
    border-radius: 6px;
    overflow: hidden;
    background: transparent;

    > .label {
      text-align: center;
      flex-basis: auto;
      min-width: 0;
      width: 100%;
      margin: 0;
      padding: 8px 12px;
      box-sizing: border-box;
      background: #e8e8e8;
      border-bottom: 1px solid #c4c4c4;
      font-weight: 600;

      &::after {
        content: '';
      }
    }

    > .value-cell {
      flex-basis: auto;
      width: 100%;
      margin: 0;
      padding: 12px;
      box-sizing: border-box;
      justify-content: center;
      text-align: center;
      background: transparent;

      div,
      .label-box,
      .value {
        display: block;
        width: 100%;
        margin: 0;
        padding: 0;
        text-align: center;
        background: transparent;
        border: none;
        font-weight: normal;
      }
    }
  }
}

.image-dialog {
  &.el-dialog {
    .el-dialog__body {
      padding-top: 0;
    }
  }
}

.image-dialog:deep() {
  &.el-dialog {
    background-color: #f2f2f2;
    border-radius: 10px;
    max-height: 90%;
    overflow: hidden;
    flex-direction: column;
    display: flex;
    margin: 0;

    &__header {
      font-weight: bold;
      border-bottom: 1px solid #bdbdbd;
      padding: 25px;
    }

    .el-carousel {
      &__item {
        justify-content: center;
        display: flex;
      }
    }

    .el-image {
      overflow: auto;

      &__inner {
        height: auto;
      }
    }
  }
}

.file-and-value {
  display: flex;
  padding: 8px 0px;

  > div {
    flex-basis: 35%;
    text-align: right;
    margin-right: 8px;
    min-width: 35%;
  }
}

.adjustment-link {
  color: blue;
  text-decoration: underline;
  &:hover {
    cursor: pointer;
  }
}
</style>
